import { Avatar as BaseAvatar } from '@base-ui/react/avatar'
import { forwardRef, type CSSProperties, type HTMLAttributes, type ImgHTMLAttributes } from 'react'
import styles from './avatar.module.css'

export interface AvatarProps extends HTMLAttributes<HTMLSpanElement> {
  src?: string
  alt?: string
  imgProps?: ImgHTMLAttributes<HTMLImageElement>
  /** Props for the fallback, which renders while the image is missing, loading or has failed */
  fallbackProps?: {
    className?: string
    style?: CSSProperties
    /** Milliseconds to wait before showing the fallback, so a picture that loads quickly never flashes it */
    delay?: number
  }
  size?: 'small' | 'medium' | 'large'
}

export const Avatar = forwardRef<HTMLSpanElement, AvatarProps>(
  ({ src, alt, imgProps, fallbackProps, size = 'medium', className, children, style, ...rest }, ref) => {
    const classes = [
      styles.avatar,
      size === 'small' ? styles.small : undefined,
      size === 'large' ? styles.large : undefined,
      className,
    ]
      .filter(Boolean)
      .join(' ')

    return (
      <BaseAvatar.Root ref={ref} className={classes} style={style} {...rest}>
        {src ? <BaseAvatar.Image src={src} alt={alt ?? ''} {...imgProps} /> : null}
        <BaseAvatar.Fallback {...fallbackProps}>{children}</BaseAvatar.Fallback>
      </BaseAvatar.Root>
    )
  }
)

Avatar.displayName = 'Avatar'
